// DB-sourced SRID definitions (Task 12). Reads `spatial_ref_sys.proj4text` for a single SRID so the
// ladder can resolve projections the bundled table doesn't carry (e.g. EPSG:2180). PostgreSQL/PostGIS
// and SpatiaLite-backed SQLite are the only engines with that catalog table — every other engine (and
// any failure, including a SQLite DB without SpatiaLite loaded) returns `null` so the ladder falls back
// to the bundled table / planar mode. A9/G2 — reached only through the lazy decode chunk.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { executeQueryV2 } from '../../../database-objects/services/queryExecutor';
import { createExecutionId } from '../../../../types/queryExecution';

// Per-(connection, srid) memo, positive AND negative (a missed SRID is asked once per session).
const cache = new Map<string, string | null>();

function cacheKey(connectionId: string, srid: number): string {
  return `${connectionId}::${srid}`;
}

/** Only engines whose catalog exposes `spatial_ref_sys.proj4text`. */
function supportsSpatialRefSys(engine: DatabaseEngineId): boolean {
  return engine === 'pgsql' || engine === 'sqlite';
}

/**
 * Fetches `proj4text` for `srid` from the connected database's `spatial_ref_sys`. pgsql + SpatiaLite
 * SQLite only; any error, empty result, or unsupported engine → `null`. Result is memoized per
 * (connection, srid), so a repeat call — including a remembered miss — never re-queries.
 */
export async function fetchDbSridDef(
  engine: DatabaseEngineId,
  connectionId: string,
  database: string,
  srid: number,
): Promise<string | null> {
  if (!supportsSpatialRefSys(engine) || !connectionId || !Number.isInteger(srid) || srid <= 0) {
    return null;
  }
  const key = cacheKey(connectionId, srid);
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  let result: string | null = null;
  try {
    // `srid` is a validated positive integer → safe to inline.
    const response = await executeQueryV2({
      connectionId,
      database,
      sql: `SELECT proj4text FROM spatial_ref_sys WHERE srid = ${srid}`,
      executionId: createExecutionId(`srid-def-${srid}`),
    });
    if (response.success) {
      const rs = response.resultSets?.[0];
      const row = rs?.rows?.[0] as Record<string, unknown> | undefined;
      if (rs && row) {
        const colKey = rs.columns[0]?.id ?? rs.columns[0]?.name ?? 'proj4text';
        const value = row[colKey];
        if (typeof value === 'string' && value.trim().length > 0) result = value.trim();
      }
    }
  } catch {
    result = null;
  }
  cache.set(key, result);
  return result;
}
